import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faCheckCircle, faSpinner } from '@/fontawesome';
import { analytics, AnalyticsEvents } from '@/services/analytics';
import { APIRoutes } from '@/services/api.routes';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useUser } from '@/contexts/UserContext';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { getPlanShortName } from '@/consts/pricingPublic';
import { clearChain, completeChainStep, continueChain, getAllOwnedMessage, markAutoContinued, type ChainProgress } from '@/services/checkoutChain';
import { showErrorToast, TOAST_MESSAGES } from '@/utils/toasts';
import { getUserFacingError } from '@/utils/errorMessages';

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 8;
/** Wartezeit nach bestätigter Aktivierung, bevor der nächste Ketten-Schritt automatisch startet. */
const AUTO_CONTINUE_DELAY_MS = 2000;

type ActivationStatus = 'polling' | 'confirmed' | 'timeout';

/**
 * Landing nach einem Stripe-Checkout (`/checkout/success?subscriptionId=…&sessionId=…`, URL
 * kommt aus dem Backend).
 *
 * Die Aktivierung wird exakt über den Status der Subscription aus der URL gepollt — nicht über
 * einen Diff der aktiven Pakete, der bei verketteten Checkouts mehrdeutig wäre.
 *
 * Gehört die Subscription zu einer Checkout-Kette (mehrere Pakete aus einem Link) und ist noch
 * ein Schritt offen, zeigt die Seite den Fortschritt und leitet nach dem Aktivierungs-Polling
 * einmalig automatisch zum nächsten Stripe-Checkout weiter; ein Button ist immer verfügbar.
 * Verlassen lässt sich die Kette hier nur, wenn der nächste Schritt nicht startet.
 */
export default function CheckoutSuccessPage() {
  const { isDarkMode } = useDarkMode();
  const { user, refetchUser } = useUser();
  const { products } = useSubscriptionProducts();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const subscriptionId = searchParams.get('subscriptionId');

  // Ketten-Schritt einmalig beim Mount abschließen (idempotent, daher StrictMode-sicher).
  // null = kein Ketten-Kauf (z.B. Einzelkauf über /pricing oder liegen gebliebene fremde Kette).
  const [progress] = useState<ChainProgress | null>(() => completeChainStep(subscriptionId));
  const nextStep = progress !== null && progress.nextStepIndex !== -1
    ? progress.chain.steps[progress.nextStepIndex]
    : null;
  const isIntermediateStep = progress !== null && nextStep !== null;

  const [activationStatus, setActivationStatus] = useState<ActivationStatus>(
    subscriptionId !== null ? 'polling' : 'confirmed'
  );
  const [continuing, setContinuing] = useState(false);
  // Einziger Ausweg aus der Kette: der nächste Schritt lässt sich nicht starten
  const [continueFailed, setContinueFailed] = useState(false);
  // Für die Anzeige „Du wirst gleich weitergeleitet“ (ein Ref allein würde keinen Re-Render auslösen)
  const [autoContinueScheduled, setAutoContinueScheduled] = useState(false);

  const refetchUserRef = useRef(refetchUser);
  const autoContinueStartedRef = useRef(false);
  const autoContinueTimerRef = useRef<number | null>(null);
  const userId = user?.id ?? null;

  useEffect(() => {
    refetchUserRef.current = refetchUser;
  }, [refetchUser]);

  useEffect(() => {
    analytics.track(AnalyticsEvents.CHECKOUT_COMPLETED, progress !== null
      ? {
        productType: progress.chain.steps[progress.stepIndex].plan,
        chainStep: progress.stepIndex + 1,
        chainTotal: progress.total,
      }
      : {});
  }, [progress]);

  // Kommt die Seite aus dem Back/Forward-Cache zurück (Browser-Zurück von Stripe), hängt sonst
  // der „Weiterleitung…“-Spinner des vorherigen Klicks fest.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setContinuing(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Warnung beim Schließen zwischen zwei Schritten. Während `continuing` aus, weil auch der
  // Redirect zu Stripe (`window.location.href`) `beforeunload` auslöst.
  useEffect(() => {
    if (!isIntermediateStep || continuing) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => { window.removeEventListener('beforeunload', handleBeforeUnload); };
  }, [isIntermediateStep, continuing]);

  // Aktivierung pollen: Subscription aus der URL muss ACTIVE werden (Webhook-Latenz).
  // Läuft auch ohne geladenen User-Kontext bis zum Timeout weiter, damit die Seite nie hängen bleibt.
  useEffect(() => {
    if (subscriptionId === null || activationStatus !== 'polling') return;

    let cancelled = false;
    let attempt = 0;
    let timeoutId: number | undefined;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      if (userId !== null) {
        try {
          const response = await APIRoutes.getUserSubscriptions(userId);
          const subscription = response.subscriptions.find(s => s.id === subscriptionId);
          if (!cancelled && subscription !== undefined && subscription.status === 'ACTIVE') {
            // Erst FeatureConfig / aktive Pakete nachladen, dann bestätigen – sonst zeigt das
            // Dashboard nach „Los geht's“ kurz den alten Stand
            try {
              await refetchUserRef.current();
            } catch (error) {
              console.error('Failed to refetch user after activation:', error);
            }
            if (!cancelled) {
              setActivationStatus('confirmed');
            }
            return;
          }
        } catch (error) {
          console.error('Failed to poll subscription activation:', error);
        }
      }
      if (cancelled) return;
      if (attempt >= POLL_MAX_ATTEMPTS) {
        // Aktivierung kam nicht in der Wartezeit – trotzdem einmal nachladen, falls sie knapp danach eintrifft
        void refetchUserRef.current();
        setActivationStatus('timeout');
        return;
      }
      timeoutId = window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    // Erster Versuch sofort – der Webhook ist häufig schon durch, wenn Stripe zurückleitet
    timeoutId = window.setTimeout(poll, 0);

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [subscriptionId, userId, activationStatus]);

  const handleContinue = useCallback(async () => {
    if (progress === null || user === null || continuing) return;
    setContinuing(true);
    const openCount = progress.chain.steps.filter(step => step.status === 'pending' || step.status === 'started').length;
    try {
      const result = await continueChain(user, progress.chain, products);
      if (result === 'DONE') {
        // Nichts mehr zu buchen (z.B. inzwischen in einem anderen Tab gekauft)
        showErrorToast(getAllOwnedMessage(openCount));
        navigate('/dashboard', { replace: true });
      }
      // REDIRECTING -> Browser geht zu Stripe, Spinner absichtlich stehen lassen
    } catch (error) {
      console.error('Continuing checkout chain failed:', error);
      setContinuing(false);
      setContinueFailed(true);
      showErrorToast(getUserFacingError(error, TOAST_MESSAGES.GENERAL_ERROR));
    }
  }, [progress, user, continuing, products, navigate]);

  const handleContinueRef = useRef(handleContinue);
  useEffect(() => {
    handleContinueRef.current = handleContinue;
  }, [handleContinue]);

  // Auto-Weiterleitung zum nächsten Schritt: einmal pro Success-Seite (Reload und Browser-Zurück
  // von Stripe lösen sie nicht erneut aus), nach Ende des Pollings — auch bei Timeout, denn
  // bezahlt ist Schritt 1 bereits; nur der Aktivierungs-Webhook ist langsam.
  useEffect(() => {
    if (autoContinueStartedRef.current) return;
    if (progress === null || nextStep === null || subscriptionId === null || user === null) return;
    if (activationStatus === 'polling') return;
    if (progress.chain.autoContinuedFor.includes(subscriptionId)) return;

    autoContinueStartedRef.current = true;
    setAutoContinueScheduled(true);
    markAutoContinued(progress.chain, subscriptionId);
    autoContinueTimerRef.current = window.setTimeout(() => {
      void handleContinueRef.current();
    }, AUTO_CONTINUE_DELAY_MS);
  }, [progress, nextStep, subscriptionId, user, activationStatus]);

  // Timer nur beim Unmount räumen (StrictMode simuliert einen Unmount: Guard zurücksetzen,
  // damit der Effekt beim Remount erneut greift)
  useEffect(() => {
    return () => {
      if (autoContinueTimerRef.current !== null) {
        window.clearTimeout(autoContinueTimerRef.current);
        autoContinueTimerRef.current = null;
      }
      autoContinueStartedRef.current = false;
    };
  }, []);

  const handleLater = () => {
    if (autoContinueTimerRef.current !== null) {
      window.clearTimeout(autoContinueTimerRef.current);
      autoContinueTimerRef.current = null;
    }
    setAutoContinueScheduled(false);
    clearChain();
    navigate('/dashboard', { replace: true });
  };

  const currentPlanName = progress !== null
    ? getPlanShortName(products, progress.chain.steps[progress.stepIndex].plan)
    : '';
  const nextPlanName = nextStep !== null ? getPlanShortName(products, nextStep.plan) : '';
  const autoContinuePending = isIntermediateStep && autoContinueScheduled && !continuing;

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-lg w-full text-center">
        <div className="mb-6">
          <img
            src={isDarkMode ? "/logo_light.png" : "/logo.png"}
            alt="Börsencampus Logo"
            className="h-12 object-contain mx-auto mb-4"
            loading="eager"
            fetchPriority="high"
          />
          <div className="w-20 h-1 bg-blue-600 mx-auto rounded-full mb-8"></div>
          <div className="w-36 h-36 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
            <FontAwesomeIcon icon={faCheck} className="text-green-500 text-6xl sm:text-7xl" />
          </div>
        </div>

        {isIntermediateStep && progress !== null ? (
          <>
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 mb-2">
              Schritt {progress.stepIndex + 1} von {progress.total} erledigt
            </p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {currentPlanName} wurde gebucht
            </h1>

            <div className="flex items-center justify-center gap-2 text-sm mb-6">
              {activationStatus === 'polling' && (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                  <span className="text-gray-600 dark:text-gray-400">Wird aktiviert…</span>
                </>
              )}
              {activationStatus === 'confirmed' && (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700 dark:text-gray-300">Aktiviert</span>
                </>
              )}
              {activationStatus === 'timeout' && (
                <span className="text-gray-600 dark:text-gray-400">
                  Die Aktivierung dauert etwas länger – das hält den nächsten Schritt nicht auf.
                </span>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left">
              <p className="font-medium text-gray-900 dark:text-gray-100 mb-1">Als Nächstes: {nextPlanName}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Für dieses Paket ist eine separate Zahlung nötig.
                {autoContinuePending ? ' Du wirst gleich automatisch zum Checkout weitergeleitet.' : ''}
              </p>
            </div>

            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => { void handleContinue(); }}
                disabled={continuing || user === null}
                className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait cursor-pointer transition-colors font-semibold text-lg"
              >
                {continuing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
                    Weiterleitung zum Checkout…
                  </>
                ) : (
                  <>
                    Jetzt weiter zu {nextPlanName}
                    <FontAwesomeIcon icon={faArrowRight} className="w-5 h-5" />
                  </>
                )}
              </button>

              {continueFailed && (
                <>
                  <button
                    type="button"
                    onClick={handleLater}
                    disabled={continuing}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline cursor-pointer disabled:cursor-not-allowed"
                  >
                    Später fortsetzen
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Du kannst den Link jederzeit erneut öffnen – bereits gebuchte Pakete werden übersprungen.
                  </p>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Du bist mit an Board!
            </h1>

            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Mit deiner Entscheidung hast du dir Zugang zu Struktur, Einordnung und einem klaren Vorgehen an den Börsen gesichert!
            </p>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6 text-left">
              <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">Wichtig vorab:</p>
              <ul className="text-gray-600 dark:text-gray-400 space-y-1 text-sm">
                <li>• Du musst jetzt nichts überstürzen</li>
                <li>• Es gibt keinen Zeitdruck – alles ist Schritt für Schritt aufgebaut</li>
                <li>• Per E-Mail und im Börsencampus erhältst du regelmäßig neue Tradingideen, Analysen und direkte Unterstützung</li>
              </ul>
            </div>

            <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
              Starte jetzt deine Reise an der Börse!
            </p>

            <div className="flex flex-col items-center gap-3">
              {activationStatus === 'polling' ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-blue-600 text-white rounded-lg opacity-60 cursor-wait font-semibold text-lg"
                >
                  <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
                  Dein Paket wird aktiviert…
                </button>
              ) : (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors font-semibold text-lg"
                >
                  Los geht's
                  <FontAwesomeIcon icon={faArrowRight} className="w-5 h-5" />
                </Link>
              )}

              {activationStatus === 'timeout' && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Sollten Features fehlen, lade die Seite bitte einmal neu.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
